import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  useMediaQuery, 
  useTheme 
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer when screen size changes
  useEffect(() => {
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const navItems = [
    { name: 'Why Orglance', path: '#' },
    { name: 'Use Cases', path: '#' },
    { name: 'Pricing', path: '#' },
    { name: 'Team', path: '#' },
    { name: 'Blog', path: '#' },
    { name: 'Docs', path: '#' },
  ];

  // Mobile layout - with increased height
  const mobileLayout = (
    <Toolbar sx={{ 
      padding: '0 16px', 
      display: 'flex', 
      justifyContent: 'space-between',
      minHeight: '70px' // Increased height for mobile
    }}>
      <Link to="/">
        <img
          src={logo}
          alt="Orglance Logo"
          style={{
            height: '45px', // Slightly larger logo
            width: 'auto',
            objectFit: 'contain'
          }}
        />
      </Link>
      
      <IconButton
        edge="end"
        color="inherit"
        aria-label="menu"
        onClick={handleDrawerToggle}
        sx={{ 
          padding: 1,
          '& .MuiSvgIcon-root': {
            fontSize: '28px' // Larger icon
          }
        }}
      >
        <MenuIcon />
      </IconButton>
    </Toolbar>
  );

  // Desktop layout - with increased height
  const desktopLayout = (
    <Toolbar sx={{ 
      padding: '0 24px', 
      display: 'flex',
      minHeight: '120px' // Increased height for desktop
    }}>
      <Box sx={{ flexGrow: 0, mr: 4 }}>
        <Link to="/">
          <img
            src={logo}
            alt="Orglance Logo"
            style={{
              height: '55px', // Larger logo for desktop
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Link>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexGrow: 1,
        gap: { md: 2, lg: 4 }
      }}>
        {navItems.map((item) => (
          <Link 
            key={item.name}
            to={item.path}
            style={{
              color: theme.palette.text.primary,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '1rem', // Slightly larger font
              position: 'relative'
            }}
          >
            {item.name}
          </Link>
        ))}
      </Box>
      
      <Box sx={{ flexGrow: 0 }}>
        <Button
          component={Link}
          to="/auth"
          variant="contained"
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: '#fff',
            padding: '10px 20px', // Larger button
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem' // Slightly larger font
          }}
        >
          Login/Register
        </Button>
      </Box>
    </Toolbar>
  );

  // Mobile drawer content
  const drawerContent = (
    <Box sx={{ width: 280, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pt: 1 }}>
        <Link to="/">
          <img
            src={logo}
            alt="Orglance Logo"
            style={{
              height: '45px', // Larger logo in drawer
              width: 'auto'
            }}
          />
        </Link>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon sx={{ fontSize: '24px' }} />
        </IconButton>
      </Box>
      
      <List sx={{ flexGrow: 1 }}>
        {navItems.map((item) => (
          <ListItem 
            button 
            key={item.name}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{ 
              mb: 1, 
              borderRadius: '8px',
              py: 1.5, // Taller list items
              fontSize: '1rem' // Larger font size
            }}
          >
            {item.name}
          </ListItem>
        ))}
      </List>
      
      <Button
        fullWidth
        component={Link}
        to="/auth"
        variant="contained"
        onClick={handleDrawerToggle}
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: '#fff',
          padding: '12px',
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          mt: 2,
          mb: 1
        }}
      >
        Login/Register
      </Button>
    </Box>
  );

  return (
    <AppBar 
      position="fixed"
      color="default"
      elevation={1} // Added slight elevation
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}
    >
      {isMobile ? mobileLayout : desktopLayout}
      
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': { 
            width: { xs: '80%', sm: 280 },
            maxWidth: '100%'
          }
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;